import React, { useState, useEffect, useCallback } from "react";

import { AdapterEvents } from "../../constants/Events";
import { EventBus } from "../../events/EventBus";
import { featureFlags } from "../../feature-flags/FeatureFlags";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface AdapterState<T = any> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface AdapterContextValue<T = any> extends AdapterState<T> {
  setData: (data: T) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

export class ContextToEventAdapter {
  private eventBus: EventBus;

  constructor(eventBus: EventBus) {
    this.eventBus = eventBus;
  }

  /**
   * Creates an adapted context provider that can switch between legacy and event-driven implementations
   */
  createAdaptedProvider<T>(
    moduleName: string,
    featureFlag: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    legacyProvider: React.ComponentType<any>,
    defaultState: T,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): React.ComponentType<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (props: any) => {
      const [state, setState] = useState<AdapterState<T>>({
        data: defaultState,
        loading: false,
        error: null,
      });

      const moduleBus = this.eventBus.getModuleBus(moduleName);

      useEffect(() => {
        if (!featureFlags.isEnabled(featureFlag) || !moduleBus) {
          return;
        }

        // Subscribe to module events for state updates
        const subscriptions = [
          moduleBus.subscribe(AdapterEvents.STATE_CHANGED, (payload) => {
            setState((prev) => ({
              ...prev,
              data: payload.data,
              loading: false,
              error: null,
            }));
          }),

          moduleBus.subscribe(AdapterEvents.LOADING_STARTED, () => {
            setState((prev) => ({
              ...prev,
              loading: true,
              error: null,
            }));
          }),

          moduleBus.subscribe(AdapterEvents.LOADING_FINISHED, () => {
            setState((prev) => ({
              ...prev,
              loading: false,
            }));
          }),

          moduleBus.subscribe(AdapterEvents.ERROR_OCCURRED, (payload) => {
            setState((prev) => ({
              ...prev,
              loading: false,
              error: payload.error || "An error occurred",
            }));
          }),
        ];

        return () => {
          subscriptions.forEach((id) => moduleBus.unsubscribe(id));
        };
      }, [moduleName, featureFlag, moduleBus]);

      const contextValue: AdapterContextValue<T> = {
        ...state,
        setData: (data: T) => {
          setState((prev) => ({ ...prev, data, error: null }));
          if (moduleBus) {
            moduleBus.emit(AdapterEvents.STATE_CHANGED, { data });
          }
        },
        setLoading: (loading: boolean) => {
          setState((prev) => ({ ...prev, loading }));
          if (moduleBus) {
            moduleBus.emit(
              loading
                ? AdapterEvents.LOADING_STARTED
                : AdapterEvents.LOADING_FINISHED,
              {},
            );
          }
        },
        setError: (error: string | null) => {
          setState((prev) => ({ ...prev, error }));
          if (moduleBus && error) {
            moduleBus.emit(AdapterEvents.ERROR_OCCURRED, { error });
          }
        },
        reset: () => {
          setState({
            data: defaultState,
            loading: false,
            error: null,
          });
          if (moduleBus) {
            moduleBus.emit(AdapterEvents.STATE_RESET, {});
          }
        },
      };

      // Use legacy provider if feature flag is disabled
      if (!featureFlags.isEnabled(featureFlag)) {
        return React.createElement(legacyProvider, props);
      }

      // Use adapted provider with event-driven state management
      return React.createElement(legacyProvider, {
        ...props,
        value: contextValue,
      });
    };
  }

  /**
   * Creates a hook that bridges context usage to event-driven system
   */
  createAdaptedHook<T>(
    moduleName: string,
    featureFlag: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    legacyHook: () => any,
    defaultState: T,
  ): () => AdapterContextValue<T> {
    return () => {
      const [state, setState] = useState<AdapterState<T>>({
        data: defaultState,
        loading: false,
        error: null,
      });

      const moduleBus = this.eventBus.getModuleBus(moduleName);
      const legacyResult = legacyHook();

      useEffect(() => {
        if (!featureFlags.isEnabled(featureFlag) || !moduleBus) {
          return;
        }

        // Subscribe to module events
        const subscriptions = [
          moduleBus.subscribe(AdapterEvents.STATE_CHANGED, (payload) => {
            setState((prev) => ({
              ...prev,
              data: payload.data,
              loading: false,
              error: null,
            }));
          }),

          moduleBus.subscribe(AdapterEvents.LOADING_STARTED, () => {
            setState((prev) => ({
              ...prev,
              loading: true,
              error: null,
            }));
          }),

          moduleBus.subscribe(AdapterEvents.ERROR_OCCURRED, (payload) => {
            setState((prev) => ({
              ...prev,
              loading: false,
              error: payload.error || "An error occurred",
            }));
          }),
        ];

        return () => {
          subscriptions.forEach((id) => moduleBus.unsubscribe(id));
        };
      }, [moduleName, featureFlag, moduleBus]);

      // Return legacy result if feature flag is disabled
      if (!featureFlags.isEnabled(featureFlag)) {
        return legacyResult;
      }

      // Return adapted result with event-driven state
      return {
        ...state,
        setData: (data: T) => {
          setState((prev) => ({ ...prev, data, error: null }));
          if (moduleBus) {
            moduleBus.emit(AdapterEvents.STATE_CHANGED, { data });
          }
        },
        setLoading: (loading: boolean) => {
          setState((prev) => ({ ...prev, loading }));
          if (moduleBus) {
            moduleBus.emit(
              loading
                ? AdapterEvents.LOADING_STARTED
                : AdapterEvents.LOADING_FINISHED,
              {},
            );
          }
        },
        setError: (error: string | null) => {
          setState((prev) => ({ ...prev, error }));
          if (moduleBus && error) {
            moduleBus.emit(AdapterEvents.ERROR_OCCURRED, { error });
          }
        },
        reset: () => {
          setState({
            data: defaultState,
            loading: false,
            error: null,
          });
          if (moduleBus) {
            moduleBus.emit(AdapterEvents.STATE_RESET, {});
          }
        },
      };
    };
  }

  /**
   * Creates a service adapter that wraps legacy service calls with event emission
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createServiceAdapter<T extends Record<string, any>>(
    moduleName: string,
    featureFlag: string,
    service: T,
  ): T {
    if (!featureFlags.isEnabled(featureFlag)) {
      return service;
    }

    const moduleBus = this.eventBus.getModuleBus(moduleName);
    if (!moduleBus) {
      return service;
    }

    return new Proxy(service, {
      get(target, prop) {
        const originalMethod = target[prop as keyof T];
        if (typeof originalMethod === "function") {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return async (...args: any[]) => {
            const eventType = `${String(prop)}`;

            // Emit request event
            moduleBus.emit(`${eventType}_REQUESTED`, { args });

            try {
              const result = await (originalMethod as Function).apply(
                target,
                args,
              );

              // Emit success event
              moduleBus.emit(`${eventType}_SUCCESS`, { data: result });

              return result;
            } catch (error) {
              // Emit error event
              moduleBus.emit(`${eventType}_FAILED`, {
                error: error instanceof Error ? error.message : String(error),
              });

              throw error;
            }
          };
        }
        return originalMethod;
      },
    });
  }

  /**
   * Creates a component adapter that wraps legacy components with event-driven props
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createComponentAdapter<T extends React.ComponentType<any>>(
    moduleName: string,
    featureFlag: string,
    Component: T,
  ): T {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const AdaptedComponent = React.forwardRef<any, any>((props, ref) => {
      const moduleBus = this.eventBus.getModuleBus(moduleName);

      const adaptedProps = useCallback(() => {
        if (!featureFlags.isEnabled(featureFlag) || !moduleBus) {
          return props;
        }

        // Add event-driven props to component
        return {
          ...props,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onEvent: (eventType: string, payload: any) => {
            moduleBus.emit(eventType, payload);
          },
          subscribeToEvent: (
            eventType: string,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            handler: (payload: any) => void,
          ) => {
            return moduleBus.subscribe(eventType, handler);
          },
        };
      }, [props, moduleName, featureFlag, moduleBus]);

      return React.createElement(Component, {
        ...adaptedProps(),
        ref,
      });
    });

    return AdaptedComponent as unknown as T;
  }
}

// Global adapter instance - will be properly initialized when used
export const contextAdapter = new ContextToEventAdapter({} as EventBus);
